import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";

const F = "w-full border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-emerald-600";
const TIER_COLORS = { inquiry: "bg-zinc-100 text-zinc-700", qualified: "bg-blue-100 text-blue-800", high_intent: "bg-emerald-100 text-emerald-800", group: "bg-purple-100 text-purple-800", urgent: "bg-red-100 text-red-800" };
const STATUSES = ["new", "opened", "responded", "quote_sent", "follow_up_due", "traveler_replied", "booked", "not_available", "lost", "invalid"];

const QuoteBuilder = ({ lead, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState({ room: lead.stay?.room_type || "", nightly_rate: "", taxes: "", meals_included: "", benefits: "", cancellation_policy: "", payment_instructions: "", booking_url: "", contact_person: "" });
  const set = (k) => (e) => setQ({ ...q, [k]: e.target.value });
  const send = async () => {
    if (!q.nightly_rate) { toast.error("Nightly rate is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/directstay/leads/${lead.id}/quotes`, {
        ...q, benefits: q.benefits.split(",").map((s) => s.trim()).filter(Boolean),
        guests: `${lead.stay?.adults || ""} adults${lead.stay?.children ? `, ${lead.stay.children} children` : ""}`,
      });
      const url = `${window.location.origin}${data.share_url}`;
      navigator.clipboard?.writeText(url).catch(() => {});
      toast.success("Quote created — secure link copied to clipboard");
      onDone(url);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create quote"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="quote-builder" className="border-t border-zinc-200 mt-3 pt-3 grid grid-cols-2 gap-2">
      <input className={F} placeholder="Room" value={q.room} onChange={set("room")} />
      <input data-testid="quote-rate" className={F} placeholder="Nightly rate * e.g. ₹4500" value={q.nightly_rate} onChange={set("nightly_rate")} />
      <input className={F} placeholder="Taxes e.g. 12% GST" value={q.taxes} onChange={set("taxes")} />
      <input className={F} placeholder="Meals included" value={q.meals_included} onChange={set("meals_included")} />
      <input className={F + " col-span-2"} placeholder="Direct-booking benefits (comma sep.)" value={q.benefits} onChange={set("benefits")} />
      <input className={F} placeholder="Cancellation policy" value={q.cancellation_policy} onChange={set("cancellation_policy")} />
      <input className={F} placeholder="Payment instructions" value={q.payment_instructions} onChange={set("payment_instructions")} />
      <input className={F} placeholder="Booking URL (optional)" value={q.booking_url} onChange={set("booking_url")} />
      <input className={F} placeholder="Contact person" value={q.contact_person} onChange={set("contact_person")} />
      <button data-testid="quote-send" onClick={send} disabled={busy} className="col-span-2 bg-emerald-700 text-white font-700 py-2 text-sm uppercase disabled:opacity-50">
        {busy ? "Creating…" : "Send quote (creates secure link)"}
      </button>
    </div>
  );
};

export const LeadInbox = () => {
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("");
  const [openQuote, setOpenQuote] = useState(null);
  const [quoteUrls, setQuoteUrls] = useState({});

  const load = () => api.get("/directstay/my/leads" + (filter ? `?status=${filter}` : "")).then((r) => setLeads(r.data.items)).catch(() => setLeads([]));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const setStatus = async (lead, status) => {
    try {
      await api.post(`/directstay/leads/${lead.id}/status`, { status });
      toast.success(`Marked ${status.replace(/_/g, " ")}`);
      load();
    } catch { toast.error("Could not update status"); }
  };

  if (!leads) return <div className="py-10 text-center text-zinc-400">Loading leads…</div>;

  return (
    <div data-testid="lead-inbox">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button onClick={() => setFilter("")} className={`text-xs px-3 py-1.5 border ${!filter ? "bg-zinc-900 text-white" : "border-zinc-300"}`}>All</button>
        {STATUSES.slice(0, 7).map((s) => (
          <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)} className={`text-xs px-3 py-1.5 border ${filter === s ? "bg-zinc-900 text-white" : "border-zinc-300"}`}>{s.replace(/_/g, " ")}</button>
        ))}
      </div>
      {leads.length === 0 && <div className="border border-dashed border-zinc-300 p-10 text-center text-zinc-500">No leads yet. Leads from your property pages will appear here.</div>}
      <div className="space-y-3">
        {leads.map((l) => (
          <div key={l.id} data-testid={`lead-card-${l.id}`} className="border border-zinc-300 bg-white p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-700 uppercase px-2 py-0.5 ${TIER_COLORS[l.tier] || "bg-zinc-100"}`}>{l.tier?.replace("_", " ")}</span>
                  <span className="text-[10px] font-700 uppercase px-2 py-0.5 bg-zinc-900 text-white">{l.status?.replace(/_/g, " ")}</span>
                  <span className="text-xs text-zinc-400">{l.property_name}</span>
                </div>
                <div className="font-heading font-900 mt-1">{l.traveler?.name} <span className="font-400 text-sm text-zinc-500">({l.traveler?.country || "—"})</span></div>
                <div className="text-sm text-zinc-600 mt-0.5">
                  {l.stay?.check_in} → {l.stay?.check_out} · {l.stay?.adults} adults{l.stay?.children ? `, ${l.stay.children} kids` : ""} · {l.stay?.rooms} room(s)
                  {l.stay?.room_type && ` · ${l.stay.room_type}`}{l.stay?.budget && ` · Budget: ${l.stay.budget}`}{l.stay?.occasion && ` · ${l.stay.occasion}`}
                </div>
                {l.stay?.special_requests && <div className="text-xs text-zinc-500 mt-1 italic">"{l.stay.special_requests}"</div>}
                <div className="text-xs text-zinc-500 mt-1">
                  <a className="text-emerald-700 hover:underline" href={`mailto:${l.traveler?.email}`}>{l.traveler?.email}</a>
                  {l.traveler?.phone && <> · <a className="text-emerald-700 hover:underline" href={`https://wa.me/${l.traveler.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer">{l.traveler.phone}{l.traveler?.whatsapp_ok ? " (WhatsApp OK)" : ""}</a></>}
                </div>
              </div>
              <div className="text-right text-xs text-zinc-400">
                <div>{new Date(l.created_at).toLocaleString()}</div>
                <div className="mt-1">Respond by {new Date(l.response_due).toLocaleString()}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {["responded", "booked", "not_available", "lost"].map((s) => (
                <button key={s} data-testid={`status-${s}-${l.id}`} onClick={() => setStatus(l, s)} className="text-xs border border-zinc-300 px-3 py-1.5 hover:border-emerald-600 hover:text-emerald-700">{s.replace(/_/g, " ")}</button>
              ))}
              <button data-testid={`quote-btn-${l.id}`} onClick={() => setOpenQuote(openQuote === l.id ? null : l.id)} className="text-xs bg-emerald-700 text-white px-3 py-1.5 font-700 uppercase">Send quote</button>
              {quoteUrls[l.id] && <a href={quoteUrls[l.id]} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 underline">View quote link</a>}
            </div>
            {openQuote === l.id && <QuoteBuilder lead={l} onDone={(url) => { setOpenQuote(null); setQuoteUrls({ ...quoteUrls, [l.id]: url }); load(); }} />}
          </div>
        ))}
      </div>
    </div>
  );
};
